/**
 * useDocumentsByCart — fetch documents for a single cart (in-flight account
 * opening). Powers the per-cart drill-in and the By Cart tab's preview cards.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DocumentRef } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { DocumentsClient } from '../services/documentsClient';

export interface UseDocumentsByCartResult {
  documents: ReadonlyArray<DocumentRef>;
  loading: boolean;
  error: string | null;
  isFixture: boolean;
  fixtureNotice: string | null;
  refresh: () => void;
}

export function useDocumentsByCart(cartId?: string): UseDocumentsByCartResult {
  const apiClient = useApiClient();
  const docsClient = useMemo(() => new DocumentsClient(apiClient), [apiClient]);
  const [docs, setDocs] = useState<ReadonlyArray<DocumentRef>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isFixture, setIsFixture] = useState<boolean>(false);
  const [fixtureNotice, setFixtureNotice] = useState<string | null>(null);
  const [tick, setTick] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);

    void (async (): Promise<void> => {
      try {
        const result = await docsClient.list({
          ...(cartId ? { cartId } : {}),
          signal: ctrl.signal,
        });
        if (cancelled) return;
        setDocs(result.documents);
        setIsFixture(result.isFixture);
        setFixtureNotice(result.fixtureNotice);
      } catch (err) {
        // PILOT 2026-09-04 — see the twin comment in useDocumentsByCif. The
        // client refuses fixture substitution in production and throws; without
        // this catch the tab spun on "loading" forever.
        if (cancelled) return;
        setDocs([]);
        setIsFixture(false);
        setFixtureNotice(null);
        setError(err instanceof Error ? err.message : 'Documents could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return (): void => {
      cancelled = true;
      ctrl.abort();
    };
  }, [docsClient, cartId, tick]);

  const refresh = useCallback((): void => {
    setTick((n) => n + 1);
  }, []);

  return { documents: docs, loading, error, isFixture, fixtureNotice, refresh };
}
